/* ──────────────────────────────────────────────────────────────
   pages/admin/manager/ProfesorVacacionesPage.tsx
   Gestión de vacaciones + ausencias  ·  MANAGER / HR
   Incluye pestañas:
     0 · Editar días
     1 · Timeline
     2 · Resumen (saldo por curso)
   ────────────────────────────────────────────────────────────── */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/en";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { useTipos } from "../../../hooks/useTiposAsignatura";
import { useCentros } from "../../../hooks/useCentros";
import { Divider } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import isBetween from "dayjs/plugin/isBetween";
import { TableContainer } from "@mui/material";
import SelectTramoDialog from "../../../components/SelectTramoDialog";
import type { Usuario } from "../../../hooks/useUsuarios";
import type { AgendaProfRow } from "../../../components/VacacionesTimeline";
import { useAusenciasProfesorReport } from "../../../hooks/useReportJob";
import type { AgendaDetRow } from "../../../components/VacacionesTimeline";
/* ── MUI ─────────────────────────────────────────── */
import {
  Stack,
  Box,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Autocomplete,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  LocalizationProvider,
  DateCalendar,
  PickersDay,
  PickersDayProps,
  TimeField,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ArrowBack, ArrowForward, Search } from "@mui/icons-material";

/* — hooks / API — */
import { useCalendario, EventoCalendario } from "../../../hooks/useCalendario";
import { useVacaciones } from "../../../hooks/useVacaciones";
import { useUsuarios } from "../../../hooks/useUsuarios";
import { useSolicitudesVacacionesBatch } from "../../../hooks/useSolicitudesVacacionesBatch";
import { useVacacionesProfesorReport } from "../../../hooks/useReportJob";
import { useAusencias, AusenciaDTO, Ausencia } from "../../../hooks/useAusencias";
import api from "../../../api/axios";
import { useAuth } from "../../../context/AuthContext";
import DlgAusenciaEdit from "../../../components/DlgAusenciaEdit";
import VacacionesTimeline from "../../../components/VacacionesTimeline";
import type { Tramo, VacacionData, IsoDate } from "../../../hooks/useVacaciones";
/* —— tipos auxiliares —— */

interface FilaPend {
  id: number;
  fecha: string;
  tramo: Tramo;
}

interface Motivo {
  id: number;
  nombreEs: string;
  slug: string;
  colorHex: string;
}

/* ═════════════ celda coloreada ═════════════ */
type DayProps = PickersDayProps<Dayjs> & {
  vac?: boolean;
  pend?: boolean;
  aus?: EventoCalendario["ausencia"];
  ausHalf?: boolean;
  sel?: boolean;
  tramo?: Tramo;
  ausCount?: number;
};

type VacProfRowExt = import("../../../api/reports").VacProfRow & {
  tipoId: number;
  centroId: number;
};

function ColoredDay({
  vac,
  pend,
  aus,
  ausHalf,
  ausCount, // ← NUEVO
  sel,
  tramo,
  ...rest
}: DayProps) {
  const theme = useTheme();

  /* ── 1 · medios días verticales (vacaciones) ───────────────────────── */
  const halfVert = tramo === "manana" || tramo === "tarde";
  const isTop = tramo === "manana"; // «tarde» ⇒ mitad inferior

  /* ── 2 · ausencias por horas ⇒ media-luna diagonal ─────────────────── */
  const ausHalfDiag =
    ausHalf || (!!aus && (aus as any).horaInicio != null && (aus as any).horaFin != null);

  /* ── 3 · colores base ──────────────────────────────────────────────── */
  const { bgColor, textColor } = (() => {
    if (vac)
      return {
        bgColor: theme.palette.info.main,
        textColor: halfVert ? theme.palette.common.black : theme.palette.info.contrastText,
      };

    if (pend)
      return {
        bgColor: theme.palette.warning.main,
        textColor: halfVert ? theme.palette.common.black : theme.palette.warning.contrastText,
      };

    if (aus) {
      const base = aus.estado === "pending" ? `${aus.colorHex}55` : aus.colorHex;
      return {
        bgColor: base,
        textColor:
          halfVert || ausHalfDiag ? theme.palette.common.black : theme.palette.common.white,
      };
    }

    if (sel)
      return {
        bgColor: theme.palette.action.selected,
        textColor: theme.palette.text.primary,
      };

    return { bgColor: "transparent", textColor: theme.palette.text.primary };
  })();

  /* ── 4 · render ─────────────────────────────────────────────────────── */
  return (
    <PickersDay
      {...rest}
      sx={{
        position: "relative",
        color: textColor,

        /* día completo → fondo sólido */
        ...(halfVert || ausHalfDiag ? {} : { bgcolor: bgColor }),

        /* ─── medio día horizontal (vacaciones) ─── */
        ...(halfVert && {
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            backgroundColor: bgColor,
            clipPath: isTop
              ? "polygon(0 0,100% 0,100% 50%,0 50%)"
              : "polygon(0 50%,100% 50%,100% 100%,0 100%)",
            zIndex: -1,
            pointerEvents: "none",
          },
        }),

        /* ─── medio día diagonal (ausencia por horas) ─── */
        ...(ausHalfDiag && {
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            backgroundImage: `linear-gradient(45deg, ${bgColor} 50%, transparent 50%)`,
            zIndex: -1,
            pointerEvents: "none",
          },
        }),
      }}
    >
      {/* badge contador de ausencias (sólo si hay más de una) */}
      {ausCount && ausCount > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 2,
            right: 2,
            bgcolor: "error.main",
            color: "common.white",
            fontSize: "0.65rem",
            px: 0.5,
            borderRadius: 1,
            lineHeight: 1.2,
          }}
        >
          +{ausCount - 1}
        </Box>
      )}
    </PickersDay>
  );
}

/* ═════════════ componente principal ═════════════ */
export default function ProfesorVacacionesPage() {
  const { t, i18n } = useTranslation<"translation">();
  const { user } = useAuth();
  const canHR = user?.roles?.some((r) => r === "admin" || r === "hr");
  const location = useLocation();
  const navProfId = (location.state as any)?.profId as number | undefined;
  const navBatchId = (location.state as any)?.batchId as number | undefined;

  /* pestañas: 0 editar · 1 timeline · 2 resumen */
  const [tab, setTab] = useState(0);
  const [dlgDelVac, setDlgDelVac] = useState<{
    open: boolean;
    fecha?: IsoDate;
  }>({
    open: false,
  });

  /* ─── profesores ─── */
  const {
    rows: profes, // mismos usos que antes
    loading: loadingProf,
    refresh: refreshProfes, // ← ① AÑADIDO
  } = useUsuarios({ sinClientes: true });

  useEffect(() => {
    // ← ② AÑADIDO
    refreshProfes(); //    primera carga de /usuarios
  }, [refreshProfes]);

  const [profSel, setProfSel] = useState<Usuario | null>(null);
  const profId = profSel?.id ?? 0;
  const [tramoDlgOpen, setTramoDlgOpen] = useState(false); // muestra diálogo
  const [tramoSel, setTramoSel] = useState<Tramo>("completo");

  /* ─── filtro Activos / Inactivos para el desplegable ─── */
  const [verInactivos, setVerInactivos] = useState(false);

  // Heurística robusta para determinar si un usuario está activo
  const esActivo = (u: Usuario) => {
    const any = u as any;
    if (typeof any.activo === "boolean") return any.activo;
    if (typeof any.isActive === "boolean") return any.isActive;
    if (typeof any.estado === "string") {
      const s = (any.estado || "").toString().toLowerCase();
      return !["inactivo", "baja", "borrado", "archivado"].includes(s);
    }
    if (any.fechaBaja) return false;
    return true; // por defecto
  };

  // Lista filtrada según el switch
  const profesFiltrados = useMemo(
    () => profes.filter((p) => (verInactivos ? !esActivo(p) : esActivo(p))),
    [profes, verInactivos]
  );

  // Si llegan navegando con un profId inactivo ⇒ cambiar automáticamente el switch
  useEffect(() => {
    if (!navProfId || !profes.length) return;
    const p = profes.find((x) => x.id === navProfId);
    if (p && !esActivo(p)) setVerInactivos(true);
  }, [navProfId, profes]);

  // Autoselección inicial con la lista filtrada (cuando se viene con navProfId)
  useEffect(() => {
    if (!navProfId || !profesFiltrados.length || profSel) return;
    const p = profesFiltrados.find((x) => x.id === navProfId);
    if (p) setProfSel(p);
  }, [navProfId, profesFiltrados, profSel]);

  // Si cambias el switch y el seleccionado ya no aparece, limpiar selección
  useEffect(() => {
    if (profSel && !profesFiltrados.some((p) => p.id === profSel.id)) {
      setProfSel(null);
    }
  }, [verInactivos, profesFiltrados, profSel]);

  /* rango curso */
  const today = dayjs();
  const fechaDesde = today.startOf("day").format("YYYY-MM-DD");
  const fechaHasta = today.add(3, "month").endOf("day").format("YYYY-MM-DD");
  const initY = today.month() >= 8 ? today.year() : today.year() - 1;
  const [startY, setStartY] = useState(initY);
  const minDate = dayjs(`${startY}-09-01`);
  const maxDate = minDate.add(1, "year").subtract(1, "day");
  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => minDate.add(i, "month")),
    [minDate]
  );
  const [tipoSel, setTipoSel] = useState<{ id: number; nombre: string } | null>(null);
  const [centroSel, setCentroSel] = useState<{
    id: number;
    nombre: string;
  } | null>(null);
  const { rows: tipos } = useTipos();
  const { rows: centros } = useCentros();
  /* ─── filtro por rol ─── */
  const [roleSel, setRoleSel] = useState<string | null>(null);

  /* todos los roles presentes en la colección de usuarios */
  const roles = useMemo(() => Array.from(new Set(profes.map((p) => p.rol))).sort(), [profes]);

  /* mapa id → rol, para filtrar las filas del timeline */
  const rolByProf = useMemo(() => new Map(profes.map((p) => [p.id, p.rol] as const)), [profes]);
  /* 1 · Vacaciones + Resumen */
  const {
    vacData, // ✅  array de objetos { fecha, tramo, … }
    resumen,
    loading: loadingVac,
    loadingResumen,
    refresh: refVac,
    refreshResumen,
    sync,
  } = useVacaciones(profId);

  const vacAprob: IsoDate[] = useMemo(() => vacData.map((v) => v.fecha as IsoDate), [vacData]);

  const setVac = useMemo(() => new Set(vacAprob), [vacAprob]);
  useEffect(() => {
    if (!profId) return;
    refVac(); // ← esto lo ve todo el mundo
    if (canHR) {
      // ← sólo HR o Admin
      refreshResumen(); // ← llamada restringida
    }
  }, [canHR, profId, startY, refVac, refreshResumen]);

  /* 2 · Eventos calendario */
  const { data: eventos = [], refetch: refreshCal } = useCalendario(
    profId,
    startY,
    { includePendientes: canHR } // ⬅️  solo HR/Admin carga “pendientes”
  );

  const setPendVac = useMemo(
    () => new Set(eventos.filter((e) => e.tipo === "vacacion_pendiente").map((e) => e.fecha)),
    [eventos]
  );
  const mapaAusEvento = useMemo(
    () =>
      new Map(
        eventos.filter((e) => e.tipo === "ausencia").map((e) => [e.fecha, e as EventoCalendario])
      ),
    [eventos]
  );

  /* 3 · Solicitudes lote pendientes */
  const {
    rows: lotes, // ← pendientes (compatibilidad)
    rowsRech: rech, // ← 🔥 NUEVO: lotes rechazados
    refresh: refreshLotes,
    aprobar: aprobarLote,
    rechazar: rechazarLote,
    aprobarFila,
    rechazarFila,
  } = useSolicitudesVacacionesBatch(profId);

  useEffect(() => {
    if (canHR && profId) refreshLotes();
  }, [canHR, profId, refreshLotes]);

  const loteActivo = navBatchId ? lotes.find((l) => l.id === navBatchId) : lotes[0];
  const mapaFilaPend = useMemo(
    () => new Map(lotes.flatMap((l) => l.filas.map((f) => [f.fecha, f]))),
    [lotes]
  );

  const [loteSel, setLoteSel] = useState<number | null>(null);
  const [loteRechSel, setLoteRechSel] = useState<number | null>(null);
  useEffect(() => {
    if (!loteSel && lotes.length) setLoteSel(lotes[0].id);
  }, [lotes, loteSel]);

  useEffect(() => {
    if (!loteRechSel && rech.length) setLoteRechSel(rech[0].id);
  }, [rech, loteRechSel]);

  const lotePend = lotes.find((l) => l.id === loteSel) || null;
  const loteRech = rech.find((l) => l.id === loteRechSel) || null;

  /* selección usuario (para altas / bajas) */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const resetSelection = () => setSelected(new Set());

  const arrancarNuevaAusencia = (fecha: IsoDate) => {
    resetSelection(); // vaciamos cualquier selección previa
    setSelected(new Set([fecha])); // seleccionamos solo ese día
    setDlgAusList({ open: false, items: [] }); // cerramos la lista-dialog
    openModal(); // abrimos el modal de nueva ausencia
  };

  const toggleSelect = (d: Dayjs) => {
    const k = d.format("YYYY-MM-DD");
    if (setPendVac.has(k) || mapaAusEvento.has(k)) return; // bloqueados
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const [range, setRange] = useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({ start: null, end: null });
  const toggleRange = (ini: Dayjs, fin: Dayjs) => {
    const days: string[] = [];
    for (let cur = ini.clone(); !cur.isAfter(fin); cur = cur.add(1, "day"))
      days.push(cur.format("YYYY-MM-DD"));
    setSelected((prev) => {
      const next = new Set(prev);
      const all = days.every((d) => next.has(d));
      all ? days.forEach((d) => next.delete(d)) : days.forEach((d) => next.add(d));
      return next;
    });
  };

  /* ausencias */
  const { rows: ausRows, add: addAusencia, refresh: refreshAus } = useAusencias(profId);

  const mapaAusList = useMemo(() => {
    const m = new Map<string, Ausencia[]>();
    ausRows.forEach((a) => {
      if (!m.has(a.fecha)) m.set(a.fecha, []);
      m.get(a.fecha)!.push(a);
    });
    return m;
  }, [ausRows]);

  const setAusHalf = useMemo(
    () =>
      new Set(ausRows.filter((a) => a.horaInicio != null && a.horaFin != null).map((a) => a.fecha)),
    [ausRows]
  );

  useEffect(() => {
    if (profId) refreshAus();
  }, [profId, refreshAus]);
  // ───────── resumen de ausencias para el curso visible ─────────
  const cursoIni = `${startY}-09-01`;
  const cursoFin = dayjs(cursoIni).add(1, "year").subtract(1, "day");
  const ausResumen = useMemo(() => {
    const mapa = new Map<number, { nombre: string; color: string; just: number; inj: number }>();

    ausRows
      .filter((a) => dayjs(a.fecha).isBetween(cursoIni, cursoFin, null, "[]"))
      .forEach((a) => {
        const id = a.motivo.id;
        if (!mapa.has(id))
          mapa.set(id, {
            nombre: a.motivo.nombreEs,
            color: a.motivo.colorHex,
            just: 0,
            inj: 0,
          });
        a.tipo === "justificada" ? mapa.get(id)!.just++ : mapa.get(id)!.inj++;
      });

    return [...mapa.values()].map((r) => ({ ...r, total: r.just + r.inj }));
  }, [ausRows, cursoIni, cursoFin]);

  /* ───────────────────────── MODAL NUEVA AUSENCIA ───────────────────────── */
  const [motivos, setMotivos] = useState<Motivo[]>([]);
  const loadMotivos = async () => {
    if (!motivos.length) {
      const { data } = await api.get<Motivo[]>("/motivos-ausencia");
      setMotivos(data);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    motivoId: null as number | null,
    tipo: "injustificada" as "justificada" | "injustificada",
    tramo: "dia" as "dia" | "horas",
    horaInicio: dayjs("09:00", "HH:mm"),
    horaFin: dayjs("13:00", "HH:mm"),
    nota: "",
  });
  const openModal = async () => {
    await loadMotivos();
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  const handleCreateAusencia = async () => {
    if (!form.motivoId) {
      alert("Motivo requerido");
      return;
    }
    const dtos: AusenciaDTO[] = [...selected].map((f) => ({
      fecha: f,
      motivoId: form.motivoId!,
      tipo: form.tipo,
      horaInicio: form.tramo === "dia" ? null : form.horaInicio.format("HH:mm"),
      horaFin: form.tramo === "dia" ? null : form.horaFin.format("HH:mm"),
      nota: form.nota || null,
    }));
    if (dtos.length === 1) await addAusencia(dtos[0]);
    else
      await api.post(
        "/ausencias",
        dtos.map((d) => ({ ...d, idProfesor: profId }))
      );
    resetSelection();
    await refreshAus();
    await refreshCal();
    closeModal();
  };

  /* diálogos varios */
  const [dlgVac, setDlgVac] = useState<{ open: boolean; fila?: FilaPend }>({
    open: false,
  });
  const [dlgBatch, setDlgBatch] = useState<{ open: boolean; motivo: string }>({
    open: false,
    motivo: "",
  });
  const [dlgAus, setDlgAus] = useState<{ open: boolean; abs?: Ausencia }>({
    open: false,
  });
  const [dlgAusList, setDlgAusList] = useState<{
    open: boolean;
    items: Ausencia[];
  }>({ open: false, items: [] });
  const [tiposAsignados, setTiposAsignados] = useState<Record<number, number[]>>({});
  const [centrosAsignados, setCentrosAsignados] = useState<Record<number, number[]>>({});

  // Cargar tipos y centros de todos los profesores visibles
  useEffect(() => {
    const cargarDatos = async () => {
      const datos = await Promise.all(
        profes.map(async (p) => {
          const [tipos, centros] = await Promise.all([
            api.get(`/profesores/${p.id}/tipos`),
            api.get(`/profesores/${p.id}/centros`),
          ]);

          return {
            id: p.id,
            tipoIds: (tipos.data ?? []).map((t: any) => t.id), // ✅ solo IDs
            centroIds: (centros.data ?? []).map((c: any) => c.idCentro),
          };
        })
      );

      const tiposMap: Record<number, number[]> = {};
      const centrosMap: Record<number, number[]> = {};

      datos.forEach((d) => {
        tiposMap[d.id] = d.tipoIds;
        centrosMap[d.id] = d.centroIds;
      });

      setTiposAsignados(tiposMap);
      setCentrosAsignados(centrosMap);
    };

    if (profes.length) cargarDatos();
  }, [profes]);

  /* click día */
  const handleDayClick = (day: Dayjs) => {
    const k = day.format("YYYY-MM-DD");

    /* 0 ▸ VACACIONES aprobadas → diálogo de borrado (solo HR/Admin) */
    if (canHR && setVac.has(k)) {
      setDlgDelVac({ open: true, fecha: k as IsoDate });
      return;
    }

    /* 1 ▸ AUSENCIAS existentes (pueden ser ≥1) */
    const ausList = mapaAusList.get(k);
    if (ausList && ausList.length) {
      ausList.length === 1
        ? setDlgAus({ open: true, abs: ausList[0] }) // una sola → editor
        : setDlgAusList({ open: true, items: ausList }); // varias → lista
      return; // nada más que hacer
    }

    /* 2 ▸ VACACIONES pendientes */
    if (setPendVac.has(k)) {
      const fila = mapaFilaPend.get(k);
      if (fila) setDlgVac({ open: true, fila });
      return;
    }

    /* 3 ▸ Selección simple / por rango (misma lógica de antes) */
    if (range.start && !range.end && day.isSame(range.start, "day")) {
      toggleSelect(day); // quita el único día
      setRange({ start: null, end: null });
      return;
    }

    if (!range.start || range.end) {
      // empieza nuevo rango
      setRange({ start: day, end: null });
    } else {
      // cierra rango
      const [ini, fin] = day.isAfter(range.start) ? [range.start, day] : [day, range.start];
      toggleRange(ini, fin);
      setRange({ start: ini, end: fin });
    }
  };

  /* guardar cambios de vacaciones */
  /* ───── guardar cambios de vacaciones (HR) ───── */
  const saveVac = async (tramoNuevo: Tramo = "completo") => {
    // 0) Nos aseguramos de tener lo último del servidor
    if (loadingVac) await refVac();

    /* 1)  Fechas ya aprobadas que el usuario ha marcado → se QUITAN */
    const quitar = vacData.filter((v) => selected.has(v.fecha));

    /* 2)  Fechas seleccionadas que NO estaban aprobadas → se AÑADEN */
    const añadirFechas = [...selected].filter((d) => !setVac.has(d));

    /* 3)  Construimos el array completo que enviamos al backend     */
    const payload: VacacionData[] = [
      // ⬅️ 1) mantenemos las que siguen vigentes
      ...vacData.filter((v) => !quitar.some((q) => q.fecha === v.fecha)),

      // ⬅️ 2) añadimos las nuevas con el tramo escogido
      ...añadirFechas.map(
        (f): VacacionData => ({
          fecha: f as IsoDate,
          tramo: tramoNuevo,
        })
      ),
    ].sort((a, b) => a.fecha.localeCompare(b.fecha));

    /* 4)  Sincronizamos y refrescamos la vista                       */
    await sync(payload);
    resetSelection();
    await refreshCal();
  };

  /* timeline */
  const { run: runVac, useStatus } = useVacacionesProfesorReport();
  const { run: runAus, useStatus: useStatusAus } = useAusenciasProfesorReport();
  const [jobAus, setJobAus] = useState<string>();
  const statusAus = useStatusAus(jobAus).data;

  const [jobVac, setJobVac] = useState<string>();
  const statusVac = useStatus(jobVac).data;
  const rowsVac = statusVac?.result?.rows ?? [];
  useEffect(() => {
    if (statusVac?.done && statusVac.result) {
    }
  }, [statusVac]);
  const agendaRows: AgendaProfRow[] = useMemo(() => {
    /* --- 1 · agrupar ausencias por profesor --- */
    const byProfAus: Record<number, AgendaDetRow[]> = {};
    (statusAus?.result?.rows ?? []).forEach((a: any) => {
      if (!byProfAus[a.profesorId]) byProfAus[a.profesorId] = [];
      byProfAus[a.profesorId].push({
        fecha: a.fecha,
        kind: "aus",
        tipoAbs: a.tipo, // 'justificada' | 'injustificada'
        motivo: a.motivo,
        horaIni: a.horaIni,
        horaFin: a.horaFin,
      });
    });

    /* --- 2 · mezclar con las vacaciones --- */
    return rowsVac.map((rv: any) => ({
      profesorId: rv.profesorId,
      profesor: rv.profesor,
      detalle: [
        ...rv.detalle.map((d: any) => ({
          fecha: d.fecha,
          kind: "vac",
          tramo: d.tramo ?? "completo",
        })),
        ...(byProfAus[rv.profesorId] ?? []),
      ],
    }));
  }, [rowsVac, statusAus]);

  const [search, setSearch] = useState("");

  const mapaTramoVac = useMemo(() => new Map(vacData.map((v) => [v.fecha, v.tramo])), [vacData]);
  const mapaTramoPend = useMemo(
    () =>
      new Map(
        lotes.flatMap((l) =>
          // en el DTO de la API cada fila ya trae su tramo
          l.filas.map((f) => [f.fecha, (f as any).tramo as Tramo | undefined])
        )
      ),
    [lotes]
  );

  /* si no hay usuario (auth) */
  if (!user) return null;

  /* ──────────────────────── JSX ──────────────────────── */
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.language.startsWith("en") ? "en" : "es"}
    >
      <Stack spacing={3}>
        <Typography variant="h5">{t("profesorVacaciones.title")}</Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={t("profesorVacaciones.tabs.editar")} />
          <Tab label={t("profesorVacaciones.tabs.timeline")} />
        </Tabs>

        {/* ───────────────────────── TAB 0 · EDITAR ───────────────────────── */}
        {tab === 0 && (
          <Grid container spacing={4}>
            {/* ░░░░░ COLUMNA IZQUIERDA ░░░░░ */}
            <Grid item xs={12} md={8} lg={9} sx={{ overflowX: "hidden", pr: { md: 0 } }}>
              {/* selector profesor + switch Activos/Inactivos */}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
                <Autocomplete
                  value={profSel}
                  loading={loadingProf}
                  options={profesFiltrados}
                  /* si el backend no rellena nombre, usamos e-mail */
                  getOptionLabel={(p: any) => p.nombre ?? p.nombreCompleto ?? p.email ?? ""}
                  onChange={(_, v) => setProfSel(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("profesorVacaciones.selectLabel")}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProf && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  /* 🔵 MÁS ANCHO (responsive) */
                  fullWidth
                  sx={{
                    width: { xs: "100%", sm: 480, md: 640, lg: 720 }, // ← ajusta aquí si quieres aún más
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={verInactivos}
                      onChange={(_, v) => setVerInactivos(v)}
                    />
                  }
                  label={
                    verInactivos
                      ? t("profesorVacaciones.toggle.inactivos", "Inactivos")
                      : t("profesorVacaciones.toggle.activos", "Activos")
                  }
                />
              </Stack>

              {profSel && (
                <>
                  {/* cabecera curso */}
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton onClick={() => setStartY((y) => y - 1)}>
                      <ArrowBack />
                    </IconButton>
                    <Typography variant="h6">
                      {startY}-{String(startY + 1).slice(2)}
                    </Typography>
                    <IconButton onClick={() => setStartY((y) => y + 1)}>
                      <ArrowForward />
                    </IconButton>
                  </Stack>

                  {/* ──────────────── CALENDARIOS (adaptativos) ──────────────── */}
                  <Box
                    sx={{
                      display: "grid",
                      gap: 4,
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      pr: { xl: 32 },
                    }}
                  >
                    {months.map((m) => (
                      <Box key={m.format()} sx={{ width: 1, minWidth: 280, mx: "auto" }}>
                        <Typography
                          variant="subtitle1"
                          align="center"
                          sx={{ mb: 1, textTransform: "capitalize" }}
                        >
                          {m.locale(i18n.language).format("MMMM")}
                        </Typography>

                        <DateCalendar
                          value={null}
                          referenceDate={m}
                          views={["day"]}
                          minDate={minDate}
                          maxDate={maxDate}
                          onChange={() => {}}
                          slots={{
                            day: ColoredDay as any,
                            calendarHeader: () => null,
                          }}
                          slotProps={{
                            day: ({ day }) => {
                              const k = day.format("YYYY-MM-DD");
                              const tramo = mapaTramoVac.get(k) ?? mapaTramoPend.get(k);
                              return {
                                vac: setVac.has(k),
                                pend: setPendVac.has(k),
                                aus: mapaAusEvento.get(k)?.ausencia,
                                ausHalf: setAusHalf.has(k),
                                ausCount: mapaAusList.get(k)?.length ?? 0,
                                sel: selected.has(k),
                                tramo,
                                onClick: () => handleDayClick(day),
                              };
                            },
                          }}
                          sx={{
                            ".MuiPickersCalendarHeader-root .MuiPickersArrowSwitcher-root": {
                              display: "none",
                            },
                            ".MuiPickersCalendarHeader-root": {
                              justifyContent: "center",
                            },
                            ".MuiDayCalendar-header": {
                              justifyContent: "center",
                            },
                            ".MuiDayCalendar-weekContainer": { minWidth: 0 },
                            ".MuiPickersDay-root": {
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              width: { xs: 28, sm: 34 },
                              height: { xs: 28, sm: 34 },
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>

                  {/* ───────── LISTA LOTES PENDIENTES (solo HR) ───────── */}
                  {canHR && lotes.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("profesorVacaciones.lotesPendientes")}
                      </Typography>

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ p: 0.5 }}>ID</TableCell>
                            <TableCell sx={{ p: 0.5 }} align="right">
                              {t("common.dias")}
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }} align="center">
                              {t("common.estado")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {lotes.map((l) => (
                            <TableRow
                              key={l.id}
                              hover
                              selected={l.id === loteSel}
                              onClick={() => setLoteSel(l.id)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell sx={{ p: 0.5 }}>{l.id}</TableCell>
                              <TableCell sx={{ p: 0.5 }} align="right">
                                {l.diasTotales ?? l.filas.length}
                              </TableCell>
                              <TableCell sx={{ p: 0.5 }} align="center">
                                {t(`estado.${l.estado}`)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}

                  {/* ───────── LISTA LOTES RECHAZADOS (solo HR) ───────── */}
                  {canHR && rech.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("profesorVacaciones.lotesRechazados", "Lotes rechazados")}
                      </Typography>

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ p: 0.5 }}>ID</TableCell>
                            <TableCell sx={{ p: 0.5 }} align="right">
                              {t("common.dias")}
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }} align="center">
                              {t("common.estado")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rech.map((l) => (
                            <TableRow
                              key={l.id}
                              hover
                              selected={l.id === loteRechSel}
                              onClick={() => setLoteRechSel(l.id)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell sx={{ p: 0.5 }}>{l.id}</TableCell>
                              <TableCell sx={{ p: 0.5 }} align="right">
                                {l.filas.length}
                              </TableCell>
                              <TableCell sx={{ p: 0.5 }} align="center">
                                {t(`estado.${l.estado}`)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {/* ───────── DÍAS DEL LOTE RECHAZADO SELECCIONADO ───────── */}
                      {loteRech && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t("profesorVacaciones.filasDelLote", {
                              id: loteRech.id,
                            })}
                          </Typography>

                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ p: 0.5 }}>{t("common.fecha")}</TableCell>
                                <TableCell sx={{ p: 0.5 }} align="center">
                                  {t("common.estado")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {loteRech.filas.map((f) => (
                                <TableRow key={f.id}>
                                  <TableCell sx={{ p: 0.5 }}>
                                    {dayjs(f.fecha).format("DD/MM/YYYY")}
                                  </TableCell>
                                  <TableCell sx={{ p: 0.5 }} align="center">
                                    {t(`estado.${f.estado}`)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* ───────── FILAS DEL LOTE SELECCIONADO ───────── */}
                  {canHR && lotePend && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t("profesorVacaciones.filasDelLote", {
                          id: lotePend.id,
                        })}
                      </Typography>

                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ p: 0.5 }}>{t("common.fecha")}</TableCell>
                            <TableCell sx={{ p: 0.5 }} align="center">
                              {t("common.estado")}
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {lotePend.filas.map((f) => (
                            <TableRow key={f.id}>
                              <TableCell sx={{ p: 0.5 }}>
                                {dayjs(f.fecha).format("DD/MM/YYYY")}
                              </TableCell>
                              <TableCell sx={{ p: 0.5 }} align="center">
                                {t(`estado.${f.estado}`)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}

                  {/* ───────── ACCIONES HR SOBRE LOTE SELECCIONADO ───────── */}
                  {canHR && lotePend && (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      sx={{ mt: 2, alignSelf: "flex-start" }}
                    >
                      <Button
                        variant="contained"
                        color="success"
                        onClick={async () => {
                          await aprobarLote(lotePend.id);
                          await Promise.all([
                            refreshLotes(),
                            refreshCal(),
                            refVac(),
                            refreshResumen(),
                          ]);
                        }}
                      >
                        {t("common.approveBatch", {
                          count: lotePend.diasTotales,
                        })}
                      </Button>

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setDlgBatch({ open: true, motivo: "" })}
                      >
                        {t("common.rejectBatch", {
                          count: lotePend.diasTotales,
                        })}
                      </Button>
                    </Stack>
                  )}

                  {/* botones acción vacaciones / ausencias */}
                  {canHR && (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={loadingVac || !selected.size}
                        onClick={() => setTramoDlgOpen(true)}
                      >
                        {t("profesorVacaciones.guardar", {
                          count: selected.size,
                        })}
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        disabled={!selected.size}
                        onClick={openModal}
                      >
                        {t("absence.crear", { count: selected.size })}
                      </Button>
                    </Stack>
                  )}
                </>
              )}
            </Grid>

            {/* ░░░░░ COLUMNA DERECHA · RESUMEN (solo HR) ░░░░░ */}
            {canHR && (
              <Grid
                item
                xs={12}
                md={4}
                lg={3}
                sx={{
                  position: { lg: "sticky", sm: "static" },
                  top: 80,
                  zIndex: 2, // ✅ “por encima” pero nunca solapa texto
                  bgcolor: "background.paper",
                }}
              >
                {/* ─────────────────── Saldo de vacaciones ─────────────────── */}
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  {t("profesorVacaciones.resumen.titulo", "Saldo de vacaciones")}
                </Typography>

                {loadingResumen ? (
                  <CircularProgress />
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      width: "100%",
                      tableLayout: { sm: "fixed", xs: "auto" }, // ❸
                      overflowX: { xs: "auto", sm: "hidden" },
                      px: 1, // un poco de “aire” lateral
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ p: 0.5, minWidth: 0 }}>
                            {t("profesorVacaciones.resumen.curso")}
                          </TableCell>
                          <TableCell sx={{ p: 0.5, minWidth: 0 }} align="right">
                            {t("profesorVacaciones.resumen.asignados")}
                          </TableCell>
                          <TableCell sx={{ p: 0.5, minWidth: 0 }} align="right">
                            {t("profesorVacaciones.resumen.disfrutados")}
                          </TableCell>
                          <TableCell sx={{ p: 0.5, minWidth: 0 }} align="right">
                            {t("profesorVacaciones.resumen.saldo")}
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {resumen.length > 0 ? (
                          resumen.map((r) => (
                            <TableRow key={r.curso}>
                              <TableCell sx={{ p: 0.5 }}>{r.curso}</TableCell>
                              <TableCell sx={{ p: 0.5 }} align="right">
                                {r.asignados}
                              </TableCell>
                              <TableCell sx={{ p: 0.5 }} align="right">
                                {r.disfrutados}
                              </TableCell>
                              <TableCell
                                sx={{
                                  p: 0.5,
                                  color: r.saldo < 0 ? "error.main" : "success.main",
                                }}
                                align="right"
                              >
                                {r.saldo}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell sx={{ p: 0.5 }} colSpan={4} align="center">
                              {t("profesorVacaciones.resumen.vacio")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Button variant="outlined" sx={{ mt: 2 }} onClick={refreshResumen}>
                  {t("profesorVacaciones.recargar")}
                </Button>

                {/* ─────────────────────── Ausencias ─────────────────────── */}
                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  {t("profesorVacaciones.ausencias.titulo", "Ausencias")}
                </Typography>

                <TableContainer
                  component={Paper}
                  sx={{
                    width: "100%",
                    tableLayout: { sm: "fixed", xs: "auto" }, // ❸
                    overflowX: { xs: "auto", sm: "hidden" },
                    px: 1,
                    mt: 1.5, // separa visualmente de la sección anterior
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ p: 0.5, minWidth: 0 }}>Motivo</TableCell>
                        <TableCell sx={{ p: 0.5, minWidth: 0 }} align="right">
                          Just.
                        </TableCell>
                        <TableCell sx={{ p: 0.5, minWidth: 0 }} align="right">
                          Injust.
                        </TableCell>
                        <TableCell sx={{ p: 0.5, minWidth: 0 }} align="right">
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {ausResumen.length > 0 ? (
                        ausResumen.map((r) => (
                          <TableRow key={r.nombre}>
                            <TableCell sx={{ p: 0.5 }}>
                              <CircleIcon fontSize="small" sx={{ color: r.color, mr: 0.75 }} />
                              {r.nombre}
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }} align="right">
                              {r.just}
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }} align="right">
                              {r.inj}
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }} align="right">
                              {r.total}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell sx={{ p: 0.5 }} colSpan={4} align="center">
                            {t("profesorVacaciones.ausencias.vacio", "Sin ausencias")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        )}

        {/* ───────────────────────── TAB 1 · TIMELINE ───────────────────────── */}
        {tab === 1 && (
          <>
            {/* ✅ Filtros por tipo y centro */}
            {/* ✅ Filtro principal por rol */}
            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", mb: 2 }}>
              <Autocomplete
                options={roles}
                value={roleSel}
                onChange={(_, v) => {
                  setRoleSel(v); // cambia el rol
                  if (v !== "profesor") {
                    // ⚠️ si deja de ser profesor ⇒ resetea filtros
                    setTipoSel(null);
                    setCentroSel(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Rol" placeholder="Todos" />
                )}
                sx={{ width: 200 }}
              />

              {/* Estos filtros solo si el rol es 'profesor' */}
              {roleSel === "profesor" && (
                <>
                  <Autocomplete
                    options={tipos}
                    getOptionLabel={(o) => o.nombre}
                    value={tipoSel}
                    onChange={(_, v) => setTipoSel(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t("profesorVacaciones.filtros.tipo")}
                      />
                    )}
                    sx={{ width: 220 }}
                  />

                  <Autocomplete
                    options={centros.filter((c) => c.nombre !== "OTHER")}
                    getOptionLabel={(o) => o.nombre}
                    value={centroSel}
                    onChange={(_, v) => setCentroSel(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={t("profesorVacaciones.filtros.centro")}
                      />
                    )}
                    sx={{ width: 220 }}
                  />
                </>
              )}
            </Stack>

            {/* filtro texto */}
            <TextField
              size="small"
              sx={{ maxWidth: 260 }}
              placeholder={t<string>("profesorVacaciones.filtro")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              sx={{ ml: 2 }}
              onClick={async () => {
                /* vacaciones → sigue igual */
                const { jobId: jVac } = await runVac({ year: today.year() });
                setJobVac(jVac);

                /* ausencias → usa el rango desde / hasta */
                const { jobId: jAus } = await runAus({
                  desde: fechaDesde,
                  hasta: fechaHasta,
                });
                setJobAus(jAus);
              }}
            >
              {t("profesorVacaciones.recargar")}
            </Button>

            {/* progreso del job */}
            {statusVac && !statusVac.done && (
              <LinearProgress sx={{ mt: 2 }} variant="determinate" value={statusVac.progress} />
            )}

            {/* timeline cuando el worker haya terminado */}
            {statusVac?.done && statusAus?.done && (
              <Box sx={{ mt: 3 }}>
                <VacacionesTimeline
                  search={search}
                  rows={agendaRows.filter((r): boolean => {
                    /* — 1 · rol — */
                    const rolOK = !roleSel || rolByProf.get(r.profesorId) === roleSel;

                    /* — 2 · filtros extra solo para “profesor” — */
                    let tipoOK = true;
                    let centroOK = true;

                    if (roleSel === "profesor") {
                      tipoOK =
                        !tipoSel || (tiposAsignados[r.profesorId] ?? []).includes(tipoSel.id);

                      centroOK =
                        !centroSel || (centrosAsignados[r.profesorId] ?? []).includes(centroSel.id);
                    }

                    return rolOK && tipoOK && centroOK;
                  })}
                />
              </Box>
            )}
          </>
        )}
      </Stack>

      {/* ───────────────── dialogs & modals (sin cambios relevantes) ───────────────── */}
      {/* diálogo fila VAC pendiente */}
      <Dialog open={dlgVac.open} onClose={() => setDlgVac({ open: false })}>
        <DialogTitle>
          {t("profesorVacaciones.dlgDia.titulo", { fecha: dlgVac.fila?.fecha })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t("profesorVacaciones.dlgDia.msg", { fecha: dlgVac.fila?.fecha })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgVac({ open: false })}>{t("common.cancel")}</Button>
          <Button
            color="error"
            onClick={() => {
              if (dlgVac.fila) {
                rechazarFila(dlgVac.fila.id).finally(() =>
                  Promise.all([refreshLotes(), refreshCal(), refVac(), refreshResumen()])
                );
              }
              setDlgVac({ open: false });
            }}
          >
            {t("common.reject")}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              if (dlgVac.fila) {
                aprobarFila(dlgVac.fila.id).finally(() =>
                  Promise.all([refreshLotes(), refreshCal(), refVac(), refreshResumen()])
                );
              }
              setDlgVac({ open: false });
            }}
          >
            {t("common.approve")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* diálogo lote RECHAZAR */}
      <Dialog open={dlgBatch.open} onClose={() => setDlgBatch({ open: false, motivo: "" })}>
        <DialogTitle>{t("profesorVacaciones.dlgBatch.titulo")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t("profesorVacaciones.dlgBatch.msg", {
              count: loteActivo?.diasTotales,
            })}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t("profesorVacaciones.dlgBatch.motivo")}
            value={dlgBatch.motivo}
            onChange={(e) => setDlgBatch((s) => ({ ...s, motivo: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgBatch({ open: false, motivo: "" })}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (loteActivo) {
                await rechazarLote(loteActivo.id, dlgBatch.motivo || undefined);
                await Promise.all([refreshLotes(), refreshCal(), refVac(), refreshResumen()]);
              }
              setDlgBatch({ open: false, motivo: "" });
            }}
          >
            {t("common.reject")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* diálogo AUSENCIA (Ver / editar) */}
      <DlgAusenciaEdit
        open={dlgAus.open}
        absence={dlgAus.abs ?? null}
        onClose={() => setDlgAus({ open: false })}
        onSaved={async () => {
          await Promise.all([refreshAus(), refreshCal(), refVac(), refreshResumen()]);
        }}
        canApprove={user.roles?.some((r) => r === "hr" || r === "admin")}
      />

      {/* modal NUEVA AUSENCIA */}
      <Dialog open={modalOpen} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>{t("absence.modal.titulo", { count: selected.size })}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {/* motivo */}
          <TextField
            select
            fullWidth
            margin="normal"
            label={t("absence.motivo")}
            value={form.motivoId ?? ""}
            onChange={(e) =>
              setForm((s) => ({
                ...s,
                motivoId: Number(e.target.value) || null,
              }))
            }
          >
            {motivos.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.nombreEs}
              </MenuItem>
            ))}
          </TextField>

          {/* tipo justificada / injustificada */}
          <RadioGroup
            row
            value={form.tipo}
            onChange={(_, v) => setForm((s) => ({ ...s, tipo: v as any }))}
            sx={{ mb: 2 }}
          >
            <FormControlLabel
              value="justificada"
              control={<Radio />}
              label={t("absence.justificada")}
            />
            <FormControlLabel
              value="injustificada"
              control={<Radio />}
              label={t("absence.injustificada")}
            />
          </RadioGroup>

          {/* tramo día / horas */}
          <RadioGroup
            row
            value={form.tramo}
            onChange={(_, v) => setForm((s) => ({ ...s, tramo: v as any }))}
            sx={{ mb: 2 }}
          >
            <FormControlLabel value="dia" control={<Radio />} label={t("absence.diaCompleto")} />
            <FormControlLabel value="horas" control={<Radio />} label={t("absence.horas")} />
          </RadioGroup>

          {form.tramo === "horas" && (
            <Stack direction="row" spacing={2}>
              <TimeField
                label={t("absence.inicio")}
                value={form.horaInicio}
                onChange={(v) => setForm((s) => ({ ...s, horaInicio: v! }))}
              />
              <TimeField
                label={t("absence.fin")}
                value={form.horaFin}
                onChange={(v) => setForm((s) => ({ ...s, horaFin: v! }))}
              />
            </Stack>
          )}

          {/* nota */}
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            margin="normal"
            label={t("absence.nota")}
            value={form.nota}
            onChange={(e) => setForm((s) => ({ ...s, nota: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCreateAusencia}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <SelectTramoDialog
        open={tramoDlgOpen}
        initial={tramoSel}
        onCancel={() => setTramoDlgOpen(false)}
        onConfirm={async (tr) => {
          setTramoSel(tr); // guarda la última elección
          setTramoDlgOpen(false); // cierra el diálogo
          await saveVac(tr); // aplica los cambios con el tramo escogido
          await Promise.all([
            // refresca todo
            refreshCal(),
            refVac(),
            refreshResumen(),
          ]);
        }}
      />
      <Dialog
        open={dlgAusList.open}
        onClose={() => setDlgAusList({ open: false, items: [] })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("absence.listaDelDia", "Ausencias del día")}</DialogTitle>

        <DialogContent dividers>
          {dlgAusList.items.map((a) => (
            <Box
              key={a.id}
              sx={{
                p: 1,
                mb: 1,
                borderRadius: 1,
                bgcolor: "action.hover",
                cursor: "pointer",
              }}
              onClick={() => {
                setDlgAus({ open: true, abs: a });
                setDlgAusList({ open: false, items: [] });
              }}
            >
              <Typography variant="subtitle2">
                {a.horaInicio && a.horaFin
                  ? `${a.horaInicio.slice(0, 5)}–${a.horaFin.slice(0, 5)}`
                  : t("absence.diaCompleto")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {a.motivo.nombreEs}
              </Typography>
            </Box>
          ))}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDlgAusList({ open: false, items: [] })}>
            {t("common.close")}
          </Button>

          {/* mostrar sólo si ese día NO tiene ausencia día-completo */}
          {!dlgAusList.items.some((a) => a.horaInicio == null && a.horaFin == null) && (
            <Button
              variant="contained"
              onClick={() => arrancarNuevaAusencia(dlgAusList.items[0].fecha as IsoDate)}
            >
              {t("absence.nueva")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={dlgDelVac.open} onClose={() => setDlgDelVac({ open: false })}>
        <DialogTitle>Eliminar día de vacaciones</DialogTitle>

        <DialogContent>
          <Typography>
            ¿Quieres eliminar las vacaciones del {dayjs(dlgDelVac.fecha).format("DD/MM/YYYY")}?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDlgDelVac({ open: false })}>Cancelar</Button>

          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              /* quitamos ese día y sincronizamos */
              const payload = vacData.filter((v) => v.fecha !== dlgDelVac.fecha);
              await sync(payload); // ya refresca vacData y resumen
              await refreshCal(); // refresca eventos calendario
              setDlgDelVac({ open: false });
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
